import React, { useMemo, useState } from 'react';
import { Input, Tooltip, Button, Typography } from 'antd';
import type { InputProps } from 'antd';
import {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import FlexBox from '../../../components/atoms/FlexBox';
import { validateQosValues } from '../validations/clwQosValidation';
import { QosValues } from '../use/useQos';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';

const { Text } = Typography;

/**
 * Props for the QosComponent.
 */
interface QosComponentProps {
  /** Current QOS values object. */
  qos: QosValues;
  /** Callback invoked when QOS values change. */
  onChange: (qos: QosValues) => void;
  /** Whether the component is disabled. */
  disabled?: boolean;
  /** Custom CSS styles. */
  style?: React.CSSProperties;
  /** Validation status for the component. */
  status?: '' | 'error' | 'warning';
  /** Callback invoked when any input receives focus. */
  onFocus?: () => void;
  /** Callback invoked when any input loses focus. */
  onBlur?: () => void;
  /** Input variant style. */
  variant?: 'outlined' | 'filled' | 'borderless';
  /** Input size. */
  size?: InputProps['size'];
  /** Display-only mode for aggregations - hides all controls, shows only the compact value. */
  displayOnly?: boolean;
}

/**
 * Default QOS values used when resetting or initializing.
 */
const DEFAULT_QOS: QosValues = {
  totalLines: 0,
  clwRepresentedLines: 0,
  clwSolelyListedLines: 0,
  clwQuotedLines: 0,
};

/**
 * Component for editing and displaying QOS (Quality of Service) values.
 * Supports both editable mode with validation and display-only mode for aggregations.
 */
export const QosComponent: React.FC<QosComponentProps> = ({
  qos = DEFAULT_QOS,
  onChange,
  disabled = false,
  style,
  status: externalStatus,
  variant = 'filled',
  size = 'middle',
  displayOnly = false,
}) => {
  const { isPdfExportMode } = usePdfExportContext();
  /** Per-field validation status for individual inputs. */
  const [inputStatus, setInputStatus] = useState<{
    [key in keyof QosValues]?: '' | 'error' | 'warning';
  }>({});

  /** Validation result computed from current QOS values. */
  const validation = useMemo(() => {
    return validateQosValues(qos ?? DEFAULT_QOS);
  }, [qos]);

  /** Status derived from validation result for input styling. */
  const derivedStatus = useMemo((): '' | 'error' | 'warning' => {
    if (validation.type === 'warning') {
      return 'warning';
    }
    if (validation.type === 'error') {
      return 'error';
    }
    return '';
  }, [validation.type]);

  /**
   * Updates the validation status for a specific field.
   * @param validationResult - Result from validateQosValues
   * @param field - Optional field name; defaults to 'totalLines'
   * @returns Whether the validation passed
   */
  const updateValidationState = (
    validationResult: ReturnType<typeof validateQosValues>,
    field?: keyof QosValues
  ) => {
    const { isValid, type } = validationResult;
    setInputStatus((prevStatus) => ({
      ...prevStatus,
      [field || 'totalLines']: type === 'warning' ? 'warning' : isValid ? '' : 'error',
    }));
    return isValid;
  };

  /**
   * Creates a change handler for a specific QOS field.
   * @param field - The QOS field to update
   * @returns Change event handler
   */
  const handleInputChange =
    (field: keyof QosValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = parseInt(e.target.value, 10) || 0;
      const newQos: QosValues =
        field === 'totalLines'
          ? { ...DEFAULT_QOS, totalLines: inputValue }
          : { ...(qos ?? DEFAULT_QOS), [field]: inputValue };

      updateValidationState(validateQosValues(newQos), field);
      onChange(newQos);
    };

  /** Resets all QOS values to defaults and clears validation status. */
  const handleReset = () => {
    onChange(DEFAULT_QOS);
    setInputStatus({} as Record<keyof QosValues, '' | 'error' | 'warning'>);
  };

  if (displayOnly) {
    const value = qos ?? DEFAULT_QOS;
    return (
      <Text style={style}>
        {value.totalLines ?? ''}
        <Text type="secondary">•</Text>
        {value.clwRepresentedLines ?? ''}
        <Text type="secondary">•</Text>
        {value.clwSolelyListedLines ?? ''}
        <Text type="secondary">•</Text>
        {value.clwQuotedLines ?? ''}
      </Text>
    );
  }

  /**
   * Renders an input field for a QOS value.
   * @param field - The QOS field key
   * @param title - Tooltip title text
   * @returns Input component wrapped in Tooltip
   */
  const renderInput = (field: keyof QosValues, title: string) => (
    <Tooltip title={title}>
      <Input
        style={{
          width: 75,
          textAlign: 'center',
        }}
        variant={variant}
        onChange={handleInputChange(field)}
        onBlur={() => {
          if (qos) {
            updateValidationState(validateQosValues(qos), field);
          }
        }}
        disabled={disabled}
        status={externalStatus || inputStatus[field] || derivedStatus}
        prefix={
          field === 'totalLines' &&
          (externalStatus || inputStatus[field] || derivedStatus) === 'warning' ? (
            <ExclamationCircleOutlined />
          ) : null
        }
        size={size}
        value={qos?.[field] ?? DEFAULT_QOS[field]}
        id={`qos-${field}`}
      />
    </Tooltip>
  );

  return (
    <FlexBox
      column
      justifyStart
      alignStart
      gap={5}
      style={style}
    >
      <FlexBox gap={10} alignStart justifyStart wrap>
        {renderInput('totalLines', 'Total Lines')}
        {renderInput('clwRepresentedLines', 'Represented Lines')}
        {renderInput('clwSolelyListedLines', 'Solely Listed')}
        {renderInput('clwQuotedLines', 'Quoting')}
        {!isPdfExportMode && (
          <Button
            htmlType="button"
            onClick={handleReset}
            icon={<CloseCircleOutlined />}
            type="link"
            disabled={disabled}
          />
        )}
      </FlexBox>
      {validation.message && (derivedStatus === 'error' || derivedStatus === 'warning') && (
        <Text type={derivedStatus === 'error' ? 'danger' : 'warning'}>
          <small>
            <b>
              {validation.message}
            </b>
          </small>
        </Text>
      )}
    </FlexBox>
  );
};
